import { useState } from 'react'
import { ArrowRight, CreditCard, Lock, AlertCircle, CheckCircle2 } from 'lucide-react'
import { cn } from '../lib/utils'

const expenses = [
  { id: 'EXP-2211', vendor: 'AWS Singapore', amount: 1240.0, submittedBy: 'J. Cruz', status: 'approved', date: '2026-08-05' },
  { id: 'EXP-2210', vendor: 'Stripe', amount: 850.0, submittedBy: 'M. Lim', status: 'pending', date: '2026-08-05' },
  { id: 'EXP-2209', vendor: 'Datadog', amount: 4320.5, submittedBy: 'A. Santos', status: 'pending', date: '2026-08-04' },
  { id: 'EXP-2208', vendor: 'Slack', amount: 315.0, submittedBy: 'R. Reyes', status: 'rejected', date: '2026-08-03' },
]

export function ExpenseDashboard() {
  const [selected, setSelected] = useState(expenses[1])
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)

  const handleCheckout = async () => {
    setLoading(true)
    setResult(null)
    // In production this calls a backend that holds Stripe secret keys server-side.
    setTimeout(() => {
      setLoading(false)
      setResult({ ok: true, id: `pi_${Math.random().toString(36).slice(2, 14)}` })
    }, 1400)
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Expense Approval & Checkout</h1>
        <p className="text-sm text-slate-500">
          Approve expenses and complete payment through a backend-secured checkout.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-slate-900">Pending Expenses</h2>
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Vendor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Submitted By</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {expenses.map((exp) => (
                  <tr
                    key={exp.id}
                    onClick={() => exp.status === 'pending' && setSelected(exp)}
                    className={cn(
                      'hover:bg-slate-50',
                      exp.status === 'pending' && 'cursor-pointer',
                      selected?.id === exp.id && 'bg-tms-50'
                    )}
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-slate-900">{exp.id}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{exp.vendor}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700">${exp.amount.toFixed(2)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-500">{exp.submittedBy}</td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'badge capitalize',
                          exp.status === 'approved' && 'bg-emerald-50 text-emerald-700',
                          exp.status === 'pending' && 'bg-amber-50 text-amber-700',
                          exp.status === 'rejected' && 'bg-rose-50 text-rose-700'
                        )}
                      >
                        {exp.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-slate-900">Checkout</h2>

          <div className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                Payments are processed through a backend endpoint. Stripe secret keys and Cloudflare tokens must not be shipped to the browser.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-500">Selected expense</label>
              <p className="text-sm font-medium text-slate-900">{selected.vendor}</p>
              <p className="text-xs text-slate-500">{selected.id}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500">Amount to pay</label>
              <p className="text-2xl font-semibold text-slate-900">${selected.amount.toFixed(2)}</p>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <CreditCard className="h-4 w-4" />
                <span>Card ending in 4242</span>
              </div>
            </div>

            <button
              onClick={handleCheckout}
              disabled={loading || selected.status !== 'pending'}
              className={cn(
                'flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white',
                selected.status === 'pending'
                  ? 'bg-tms-600 hover:bg-tms-700'
                  : 'cursor-not-allowed bg-slate-300'
              )}
            >
              {loading ? (
                <>Processing...</>
              ) : (
                <>
                  <Lock className="h-4 w-4" />
                  Pay ${selected.amount.toFixed(2)} <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>

            {result?.ok && (
              <div className="flex items-start gap-2 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                <p>
                  Payment intent created: <code className="rounded bg-emerald-100 px-1">{result.id}</code>. (Backend will confirm.)
                </p>
              </div>
            )}
          </div>
        </section>
      </div>

      <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="mb-3 text-lg font-semibold text-slate-900">Secure Integration Notes</h2>
        <ul className="list-disc space-y-2 pl-5 text-sm text-slate-700">
          <li>
            Frontend collects card details via Stripe Elements and obtains a{' '}
            <code className="rounded bg-slate-100 px-1">payment_method</code> token.
          </li>
          <li>
            The token is sent to a backend endpoint (e.g.{' '}
            <code className="rounded bg-slate-100 px-1">POST /api/payments/checkout</code>) which holds the Stripe secret key.
          </li>
          <li>
            Cloudflare tokens, if used for asset proxy or turnstile, should also be managed server-side or via environment variables injected at build time by a secure CI/CD system.
          </li>
          <li>
            A template config file is included in the repo at{' '}
            <code className="rounded bg-slate-100 px-1">src/config/payments.config.template.js</code>. Copy it, fill values from your vault, and never commit the populated file.
          </li>
        </ul>
      </section>
    </div>
  )
}
